<template>
  <div class="header-description-section" v-if="!showAuthSection">
    <!-- Cart Header -->
    <div class="cart-header">
      <div class="cart-header-content">
        <h1 class="cart-title">
          {{ quoteGenerated ? "Quote Generated" : "Your Makercart" }}
        </h1>
        <p class="cart-subtitle">
          {{
            quoteGenerated
              ? "Your quote has been sent to your email"
              : "Build a cart with classroom-ready, hands-on activities."
          }}
        </p>
      </div>
      <div class="cart-actions">
        <template v-if="!quoteGenerated">
          <div class="popup-container" @click.stop>
            <button
              class="btn-view-details"
              :class="{ active: showDetailsMenu }"
              @click="toggleDetailsMenu"
            >
              <img
                src="/images/icon/question-mark-circle.svg"
                alt="View"
                class="btn-icon"
                :style="{
                  filter: showDetailsMenu ? 'brightness(0) invert(1)' : 'none',
                }"
              />
              <span>View details</span>
            </button>

            <transition name="popup-fade">
              <div v-if="showDetailsMenu" class="details-popup">
                <div class="popup-content">
                  <h3 class="popup-title">Why choose Makercart?</h3>
                  <ul class="popup-list">
                    <li>Get the best per-student rate with bundled pricing</li>
                    <li>
                      One checkout, one package — less hassle for teachers
                    </li>
                    <li>
                      Comes with curated teaching tips and optional classroom
                      posters
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>
          <button class="btn-add-cart" @click="$emit('addNewCart')">
            <svg
              width="18"
              height="19"
              viewBox="0 0 18 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 1.49756V17.4976M17 9.49756L1 9.49756"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>Add New Makercart</span>
          </button>
        </template>
        <template v-else>
          <button class="btn-view-details">
            <img
              src="/images/icon/question-mark-circle.svg"
              alt="View"
              class="btn-icon"
            />
            <span>View activity/details</span>
          </button>
          <button class="btn-add-cart" @click="$emit('addActivity')">
            <svg
              width="18"
              height="19"
              viewBox="0 0 18 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 1.49756V17.4976M17 9.49756L1 9.49756"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>Explore Catalog</span>
          </button>
        </template>
      </div>
    </div>

    <!-- Tip Banner -->
    <div class="tip-banner" :class="{ 'success-banner': isSuccessBanner }">
      <img
        src="/images/icon/question-mark-circle.svg"
        alt="Tip"
        class="tip-icon"
      />
      <span class="tip-text">{{ currentTipMessage }}</span>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useCart } from "@/composeables/useCart";

const props = defineProps({
  quoteGenerated: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    default: "US",
  },
  hasBundledActivities: {
    type: Boolean,
    default: false,
  },
  tipBannerMessage: {
    type: String,
    default: "",
  },
  showAuthSection: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["addNewCart", "addActivity"]);

const showDetailsMenu = ref(false);

const toggleDetailsMenu = () => {
  showDetailsMenu.value = !showDetailsMenu.value;
};

const handleClickOutside = (event) => {
  if (showDetailsMenu.value && !event.target.closest(".popup-container")) {
    showDetailsMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

const { hasFullActivityCart } = useCart();

const isSuccessBanner = computed(() => {
  if (props.showAuthSection) return false;

  return (
    props.locale === "SG" && hasFullActivityCart.value && !props.quoteGenerated
  );
});

const currentTipMessage = computed(() => {
  if (props.locale === "SG" && props.quoteGenerated) {
    return "Thank you for requesting a quote for your activities. You will be contacted by edm8ker within 24 hours to discuss the next steps.";
  }
  if (isSuccessBanner.value) {
    return "Congratulations! You’ve gathered enough activities to request a quote. You can also add more activities or explore new Makercarts.";
  }
  return props.tipBannerMessage;
});
</script>

<style scoped>
@import "@/assets/css/colors.css";

.header-description-section {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* Cart Header */
.cart-header {
  background-color: var(--white);
  border: 1px solid var(--white-outline);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.cart-header-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cart-title {
  font-family: "League Spartan", sans-serif;
  font-size: 36px;
  font-weight: 700;
  line-height: 44px;
  color: var(--primary-blue);
  margin: 0;
}

.cart-subtitle {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 400;
  line-height: 32px;
  color: var(--primary-blue);
  margin: 0;
}

.cart-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-view-details {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 700;
  line-height: 24px;
  color: var(--primary-blue);
  transition: all 0.3s ease;
}

.btn-view-details.active {
  background-color: #0478ce;
  color: white;
}

.btn-view-details:hover {
  background-color: rgba(0, 61, 106, 0.05);
}

.btn-view-details.active:hover {
  background-color: #0366b3;
}

.btn-add-cart {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  border: none;
  background-color: var(--primary-blue);
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 700;
  line-height: 24px;
  color: var(--white);
}

.btn-icon {
  width: 24px;
  height: 24px;
}

/* Tip Banner */
.tip-banner {
  background-color: var(--purple-dark-1);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.tip-banner.success-banner {
  background-color: var(--green-dark-1);
}

.tip-icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) invert(1);
}

.tip-text {
  font-family: "League Spartan", sans-serif;
  font-size: var(--font-size-headline-small);
  font-weight: 400;
  line-height: 32px;
  color: var(--white);
  flex: 1;
}

/* Popup Container */
.popup-container {
  position: relative;
  display: inline-block;
}

/* Details Popup Styles */
.details-popup {
  position: absolute;
  top: 100%;
  left: -300px;
  margin-top: 8px;
  background-color: var(--white);
  border-radius: 12px;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  min-width: 500px;
  z-index: 1000;
}

/* Popup Transition */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: all 0.3s ease;
}

.popup-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.popup-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.popup-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.popup-title {
  font-family: "League Spartan", sans-serif;
  font-size: 36px;
  font-weight: 700;
  line-height: 44px;
  color: var(--primary-blue);
  margin: 0;
  text-align: center;
}

.popup-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  padding-left: 24px;
}

.popup-list li {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 500;
  color: var(--black-body-text);
  position: relative;
  padding-left: 21px;
  margin: 0px;
}

.popup-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--black-body-text);
  font-weight: bold;
}

@media (max-width: 768px) {
  .cart-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .header-description-section {
    gap: 16px;
  }

  .cart-header-content {
    width: 100%;
    border: 1px solid #d8dae5;
    padding: 8px 16px;
    border-radius: 16px;
    gap: 0px;
  }

  .cart-title {
    font-size: var(--fs-xxl);
    line-height: 36px;
  }

  .cart-subtitle {
    font-size: var(--fs-big);
    line-height: 28px;
    text-align: left;
  }

  .cart-actions {
    display: none;
  }

  .tip-text {
    font-size: var(--fs-big);
    line-height: 28px;
  }

  /* Popup Responsive */
  .details-popup {
    max-width: 100%;
    min-width: 280px;
  }

  .popup-title {
    font-size: var(--fs-xxl);
    line-height: 36px;
  }

  .popup-list li {
    font-size: var(--fs-medium);
    line-height: 24px;
  }
}
</style>
